import React, { useEffect, useState } from "react";
import { bankDone, openConnectBank } from "../lib/connectBank";
import { FaInstagram } from "react-icons/fa";
import { MdVerified } from "react-icons/md";
import { usersApi } from "../services/users";
import { startInstagramLogin, onInstagramBrowserClosed } from "../lib/instagramLogin";
import type { IgVerifyStatus } from "../lib/instagramLogin";
import type { User } from "../types/User";
import "./QrGateCard.css";
import { t as tr } from "../i18n";
import { T } from "../i18n/T";

// THE IG GATE, talent side: the card on the QR tab until the founder approves
// the profile. Four server-decided states (User.approvalStatus); "approved"
// never renders this.
//
//   needs_instagram  "Verify your Instagram" — one tap signs in to Instagram
//                    and comes back verified; typing a handle, or adding
//                    "something else" (a link), are the fallbacks
//   in_review        "You're in review" — Edit reopens the field
//   declined         a plain note ABOVE the real QR, never over it (founder,
//                    1.5.1: a declined talent keeps their free page and QR),
//                    with a clear next step

// instagram.com/handle, @handle or handle → handle. Instagram's own rule:
// letters, digits, dots, underscores, 30 max.
export function cleanInstagramHandle(raw: string): string | null {
  const m = raw.trim().match(/^(?:https?:\/\/)?(?:www\.)?instagram\.com\/([^/?#]+)/i);
  const h = (m ? m[1] : raw.trim()).replace(/^@+/, "");
  return /^[A-Za-z0-9._]{1,30}$/.test(h) ? h : null;
}

export function cleanLink(raw: string): string | null {
  const t = raw.trim();
  if (!t) return null;
  const url = /^https?:\/\//i.test(t) ? t : `https://${t}`;
  try {
    const u = new URL(url);
    return u.hostname.includes(".") ? u.toString() : null;
  } catch {
    return null;
  }
}

interface Props {
  profile: User;
  onSaved: (patch: Partial<User>) => void;
  /** How the last Instagram round trip ended (?igVerify=… on the way back). */
  igStatus?: IgVerifyStatus | null;
}

// A line only where one earns its place (founder, 2026-09-24). "done" needs
// nothing — they land on the in-review card. Cancelled and a personal account
// say nothing either: instead of explaining, the card quietly becomes the
// typed-handle one (SILENT_FALLBACK below), which is the way forward anyway.
const IG_NOTE: Partial<Record<IgVerifyStatus, string>> = {
  taken: tr("qrGateCard.thisInstagramIsAlready"),
  expired: tr("qrGateCard.thatDidnTGo"),
  failed: tr("qrGateCard.thatDidnTGo"),
};

// Instagram could not do it: closed the sheet, a personal account (Meta's
// login refuses those), or the account already belongs to someone else.
// Rather than a first screen cluttered with escape hatches, the escape hatch
// appears at the only moment it matters.
// (Since the free tier the QR tab mounts this card only for a DECLINED talent,
// which returns before the sign-in: today the sign-in runs from Edit profile
// only, and the "Verify with a DM" backup for a personal account lives there —
// ProfilePage → TalentIgConnect, 1.5.3.)
const SILENT_FALLBACK: IgVerifyStatus[] = ["cancelled", "personal", "taken"];

export default function QrGateCard({ profile, onSaved, igStatus = null }: Props) {
  const status = profile.approvalStatus;
  const handle = (profile.instagram || "").trim().replace(/^@+/, "");
  const firstLink = (profile.links || [])[0] || "";
  // The handle Meta vouched for. It only counts while it still matches the
  // handle on the profile — the server voids it on any other edit.
  const verifiedName = (profile.instagramVerification?.username || "").trim();
  const isVerified = !!verifiedName && verifiedName.toLowerCase() === handle.toLowerCase();
  const loginEnabled = !!profile.instagramLoginEnabled;

  const [editing, setEditing] = useState(false);
  const [mode, setMode] = useState<"login" | "instagram" | "link">(() => {
    if (handle || !firstLink) {
      if (!loginEnabled) return "instagram";
      return igStatus && SILENT_FALLBACK.includes(igStatus) ? "instagram" : "login";
    }
    return "link";
  });
  const [opening, setOpening] = useState(false);
  const [note, setNote] = useState<string>(() => (igStatus ? IG_NOTE[igStatus] || "" : ""));
  const [value, setValue] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  // The talent closed Instagram's sheet: let the button work again. The QR
  // page re-reads the profile on its own when the app comes back to the front.
  useEffect(() => onInstagramBrowserClosed(() => setOpening(false)), []);

  const openInstagram = async () => {
    setOpening(true);
    setNote("");
    const started = await startInstagramLogin("qr");
    if (!started) {
      setNote(tr("qrGateCard.thatDidnTGo"));
      setOpening(false);
    }
  };

  if (status === "declined") {
    return (
      <div className="qr-gate-card qr-gate-note" role="note" aria-label={tr("qrGateCard.profileNotVerified")}>
        <h3 className="qr-gate-title">{tr("qrGateCard.weCouldnTVerify")}</h3>
        <p className="qr-gate-text">
          <T k="qrGateCard.questionsHelloRequestaApp" tags={{ 0: (c) => <a href="mailto:hello@requesta.app">{c}</a> }} />
        </p>
      </div>
    );
  }

  // Bank setup unfinished: review hasn't started and can't — say so, with the
  // one button that fixes it (opens the Connect Bank sheet, or MassPay's in a
  // MassPay country). A MassPay talent whose ID check passed is done here
  // although Stripe never was.
  if (profile.isStripeOnboarded === false && !bankDone(profile)) {
    return (
      <div className="qr-gate-card" role="dialog" aria-label={tr("qrGateCard.connectYourBank")}>
        <h3 className="qr-gate-title">{tr("qrGateCard.connectYourBankTo")}</h3>
        <p className="qr-gate-text">{tr("qrGateCard.weReviewEveryProfile")}</p>
        <button type="button" className="qr-gate-btn" onClick={() => openConnectBank(profile)}>
          {tr("qrGateCard.addBankDetails")}
        </button>
      </div>
    );
  }

  if (status === "in_review" && !editing) {
    return (
      <div className="qr-gate-card" role="dialog" aria-label={tr("qrGateCard.inReview")}>
        <h3 className="qr-gate-title">{tr("qrGateCard.youReInReview")}</h3>
        <p className="qr-gate-text">{tr("qrGateCard.weLlNotifyYou")}</p>
        <p className="qr-gate-meta">
          <span className="qr-gate-handle">{handle ? `@${handle}` : firstLink.replace(/^https?:\/\//, "")}</span>
          {isVerified && <MdVerified className="qr-gate-verified" aria-label={tr("qrGateCard.verified")} />}
          {" · "}
          <button
            type="button"
            className="qr-gate-link"
            onClick={() => {
              setValue(handle || firstLink);
              setError("");
              setEditing(true);
            }}
          >
            {tr("qrGateCard.edit")}
          </button>
        </p>
      </div>
    );
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleaned = mode === "instagram" ? cleanInstagramHandle(value) : cleanLink(value);
    if (!cleaned) {
      setError(mode === "instagram" ? tr("qrGateCard.thatDoesnTLook") : tr("qrGateCard.thatDoesnTLook2"));
      return;
    }
    const form = new FormData();
    if (mode === "instagram") form.append("instagram", cleaned);
    // The server replaces the whole list, so keep any other links they have.
    else form.append("links", JSON.stringify([cleaned, ...(profile.links || []).slice(1)]));
    setSaving(true);
    try {
      const saved = await usersApi.update(profile.slug, form);
      onSaved({
        instagram: saved.instagram,
        links: saved.links,
        // The server voids a badge whose handle was edited away; mirror that
        // here so the card can never show a stale "verified".
        instagramVerification: saved.instagramVerification ?? null,
        approvalStatus: saved.approvalStatus || "in_review",
      });
      setEditing(false);
    } catch {
      setError(tr("qrGateCard.thatDidnTSave"));
    } finally {
      setSaving(false);
    }
  };

  // One tap, no typing: Instagram tells us who they are.
  if (mode === "login") {
    return (
      <div className="qr-gate-card" role="dialog" aria-label={tr("qrGateCard.verifyYourInstagram")}>
        <span className="qr-gate-ig" aria-hidden>
          <FaInstagram />
        </span>
        <h3 className="qr-gate-title">
          {tr("qrGateCard.verifyYourInstagram")}
          <MdVerified className="qr-gate-verified" aria-hidden />
        </h3>
        <button type="button" className="qr-gate-igbtn" onClick={openInstagram} disabled={opening}>
          <FaInstagram aria-hidden />
          {opening ? tr("qrGateCard.openingInstagram") : tr("qrGateCard.continue")}
        </button>
        {note ? (
          <p className="qr-gate-hint" role="status">{note}</p>
        ) : (
          <p className="qr-gate-hint">{tr("qrGateCard.thisIsHowWe")}</p>
        )}
      </div>
    );
  }

  const swap = () => {
    setMode(mode === "instagram" ? "link" : "instagram");
    setValue("");
    setError("");
  };

  return (
    <form className="qr-gate-card" onSubmit={submit} aria-label={tr("qrGateCard.verifyYourProfile")}>
      {mode === "instagram" && (
        <span className="qr-gate-ig" aria-hidden>
          <FaInstagram />
        </span>
      )}
      <h3 className="qr-gate-title">
        {mode === "instagram" ? tr("qrGateCard.verifyYourInstagram") : tr("qrGateCard.addSomethingElse")}
        {mode === "instagram" && <MdVerified className="qr-gate-verified" aria-hidden />}
      </h3>
      <label className="qr-gate-field">
        {mode === "instagram" && <span className="qr-gate-at" aria-hidden>@</span>}
        <input
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setError("");
          }}
          placeholder={mode === "instagram" ? "username" : tr("qrGateCard.facebookYoutubeSoundcloudLink")}
          aria-label={mode === "instagram" ? tr("qrGateCard.instagramUsername") : tr("qrGateCard.link")}
          inputMode={mode === "instagram" ? "text" : "url"}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          maxLength={mode === "instagram" ? 60 : 200}
        />
      </label>
      {mode === "instagram" && !error && (
        <p className="qr-gate-hint" role={note ? "status" : undefined}>
          {note || tr("qrGateCard.thisIsHowWe")}
        </p>
      )}
      {error && <p className="qr-gate-error" role="alert">{error}</p>}
      <button type="submit" className="qr-gate-btn" disabled={saving || !value.trim()}>
        {saving ? tr("qrGateCard.saving") : tr("qrGateCard.continue")}
      </button>
      <button type="button" className="qr-gate-link" onClick={swap}>
        {mode === "instagram" ? tr("qrGateCard.noInstagram") : tr("qrGateCard.useInstagramInstead")}
      </button>
    </form>
  );
}
